import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/ui/Modal/Modal';
import { Field } from '@/components/ui/Field/Field';
import { TextInput } from '@/components/ui/TextInput/TextInput';
import { Button } from '@/components/ui/Button/Button';
import { useEmailEntry } from '@/services/auth';
import { nb } from '@/i18n/nb';
import s from './AccessForm.module.css';

const schema = z
  .object({
    mode: z.enum(['login', 'request']),
    email: z.string().min(1, nb.auth.form.emailRequired).email(nb.auth.form.emailInvalid),
    name: z.string().max(64, nb.auth.form.nameMax).optional(),
    host: z.string().max(96, nb.auth.form.hostMax).optional(),
  })
  .superRefine((value, ctx) => {
    // name + vert are required only in "request" mode — a returning user logs in with
    // email alone (GATE-1 run-2 AC-4).
    if (value.mode !== 'request') return;
    if (!value.name || value.name.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['name'], message: nb.auth.form.nameRequired });
    }
    if (!value.host || value.host.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['host'], message: nb.auth.form.hostRequired });
    }
  });

type Values = z.infer<typeof schema>;

type AccessFormProps = {
  open: boolean;
  onClose: () => void;
};

// The unified "Logg inn / be om tilgang" form, with two user-selected modes: login
// (email only) and request (email + name + vert). The server gives an identical reply
// for both so it can't reveal which emails have accounts (no enumeration).
export function AccessForm({ open, onClose }: AccessFormProps) {
  const [sent, setSent] = useState(false);
  const emailEntry = useEmailEntry();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { mode: 'login', email: '', name: '', host: '' },
  });

  const mode = watch('mode');
  const isRequest = mode === 'request';

  function close() {
    onClose();
    setSent(false);
    reset();
  }

  function switchMode(next: Values['mode']) {
    setValue('mode', next);
    clearErrors();
  }

  const onSubmit = handleSubmit((values) => {
    const payload = isRequest
      ? { email: values.email, name: values.name, host: values.host }
      : { email: values.email };
    emailEntry.mutate(payload, { onSuccess: () => setSent(true) });
  });

  return (
    <Modal open={open} onClose={close} title={nb.auth.form.title}>
      {sent ? (
        <div className={s.success} role="status">
          <p>{nb.auth.emailEntryResponse}</p>
          <Button variant="primary" onClick={close}>
            {nb.common.close}
          </Button>
        </div>
      ) : (
        <form className={s.form} onSubmit={onSubmit} noValidate>
          <div className={s.modeToggle} role="group" aria-label={nb.auth.form.modeLabel}>
            <button
              type="button"
              className={`${s.modeBtn} ${mode === 'login' ? s.modeBtnOn : ''}`}
              aria-pressed={mode === 'login'}
              onClick={() => switchMode('login')}
            >
              {nb.auth.form.modeLogin}
            </button>
            <button
              type="button"
              className={`${s.modeBtn} ${isRequest ? s.modeBtnOn : ''}`}
              aria-pressed={isRequest}
              onClick={() => switchMode('request')}
            >
              {nb.auth.form.modeRequest}
            </button>
          </div>

          <p className={s.lede}>{isRequest ? nb.auth.form.requestLede : nb.auth.form.loginLede}</p>

          <Field label={nb.auth.form.emailLabel} error={errors.email?.message}>
            {(wiring) => (
              <TextInput
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="navn@eksempel.no"
                invalid={Boolean(errors.email)}
                {...wiring}
                {...register('email')}
              />
            )}
          </Field>

          {isRequest && (
            <>
              <Field label={nb.auth.form.nameLabel} error={errors.name?.message}>
                {(wiring) => (
                  <TextInput
                    autoComplete="name"
                    placeholder="Fornavn Etternavn"
                    invalid={Boolean(errors.name)}
                    {...wiring}
                    {...register('name')}
                  />
                )}
              </Field>
              <Field
                label={nb.auth.form.hostLabel}
                help={nb.auth.form.hostHelp}
                error={errors.host?.message}
              >
                {(wiring) => (
                  <TextInput
                    autoComplete="off"
                    invalid={Boolean(errors.host)}
                    {...wiring}
                    {...register('host')}
                  />
                )}
              </Field>
            </>
          )}

          {emailEntry.isError && (
            <p className={s.formError} role="alert">
              {nb.errors.generic}
            </p>
          )}
          <div className={s.actions}>
            <Button type="button" variant="secondary" onClick={close}>
              {nb.common.cancel}
            </Button>
            <Button type="submit" variant="primary" loading={emailEntry.isPending}>
              {isRequest ? nb.auth.form.requestSubmit : nb.auth.form.loginSubmit}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
